<script setup lang="ts">
import type { BoothData } from '@/types/booth'
import { BoothColors } from '@/lib/colors'

interface Props {
  data: BoothData
  index: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'click', value: MouseEvent): void
}>()

function getBoothCategoryInitial(category: string) {
  return { "その他": "他" }[category] ?? category.slice(0, 1)
}

function getImagePath(images: Record<string, unknown>) {
  const image = Object.entries(images).find(([path, _]) => {
    const mather = `/public/img/booth/${props.data['祭典名']}/${props.data['祭典名']}_`
    if (!path.includes(mather)) return false
    const numbers = path.replace(mather, '').split('.').slice(0, -1).map(Number)
    return numbers.includes(props.data['No.'])
  }) as [string, string]
  return image[1].replace(/\?t=.+/, '')
}

const images = import.meta.glob("~/public/img/booth/**/*",  { eager: true, import: "default" })
</script>

<template>
  <div :id="String(index + 1)" class="pb-2 w-full shadow hover:opacity-80 transition-opacity cursor-pointer shadow-gray-400 rounded overflow-hidden max-w-[400px]" @click="emit('click', $event)">
    <div 
      class="h-1.5 mb-3"
      :style="{ 'background-color': BoothColors[data['祭典名']]}"
    />
    <div class="mr-1 p-0 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0">
      <div class="row-span-2 grid place-items-center">
        <div 
          class="w-[60px] h-[60px] grid place-items-center rounded-r text-white text-4xl"
          :style="{ 'background-color': BoothColors[data['祭典名']] 
        }">
          {{ getBoothCategoryInitial(props.data["分類"]) }}
        </div>
      </div>
      <h3 class="text-md flex flex-wrap justify-center gap-x-2 text-center">
        <span v-for="text in props.data['団体名'].split(';')">
          {{ text }}
        </span>
      </h3>
      <h2 class="text-2xl flex flex-wrap justify-center gap-x-2 text-center">
        <span v-for="text in props.data['企画名'].split(';')">
          {{ text }}
        </span>
      </h2>
    </div>
    <div class="col-span-3 divider my-0" />
    <div class="grid grid-cols-[1fr_minmax(0,min(35%,124px))] my-1 gap-2">
      <div class="mx-1 p-0 grid grid-cols-[auto_1fr] gap-y-4 gap-x-0 h-fit">
        <div>
          <div class="text-lg whitespace-nowrap bg-gray-500 text-white rounded px-1.5 py-0.5">場所</div>
        </div>
        <div class="ml-2 text-lg mt-0.5 flex flex-wrap gap-x-2 break-all">
          <span v-for="text in props.data['建物名'].split(';')">
            {{ text }}
          </span>
          <span v-for="text in props.data['部屋・フロア'].split(';')">
            {{ text }}
          </span>
        </div>
        <div>
          <div class="text-lg whitespace-nowrap bg-gray-500 text-white rounded px-1.5 py-0.5">時間</div>
        </div>
        <div class="ml-2 text-lg flex flex-wrap gap-x-4 gap-y-2">
          <div class="grid grid-cols-[32px_1fr] grid-rows-[repeat(auto-fill,auto)]">
            <div class="h-[32px] text-sm text-blue-600 m-[1px] rounded-full border border-blue-600 border-solid grid place-items-center">
              土
            </div>
            <div v-for="text in props.data['時間(土曜)'].split(';')" class="col-[2] grid place-items-center ml-1.5 min-h-[32px]">
              {{ text }}
            </div>
          </div>
          <div class="grid grid-cols-[32px_1fr] grid-rows-[repeat(auto-fill,auto)]">
            <div class="h-[32px] text-sm text-red-600 m-[1px] rounded-full border border-red-600 border-solid grid place-items-center">
              日
            </div>
            <div v-for="text in props.data['時間(日曜)'].split(';')" class="col-[2] grid place-items-center ml-1.5 min-h-[32px] ">
              {{ text }}
            </div>
          </div>
        </div>
      </div>
      <div class="grid justify-end items-center">
        <img 
          class="max-h-[124px]" 
          :src="getImagePath(images)"
          decoding="async"
          loading="lazy"
        >
      </div>
    </div>
  </div>
</template>
